<template>
    <div class="voice-recorder">
      <div class="recorder-controls">
        <button
          class="record-button"
          :class="{ 'is-recording': isRecording }"
          @click="toggleRecording"
          :disabled="isProcessing"
          :style="{ backgroundColor: isRecording ? speakerColor : '#e53e3e' }"
        >
        <div class="button-content" title="Recording your voice will replace the default AI voice for this segment. When playing the content, users will hear your recorded voice instead.">
          <i class="record-icon" :class="{ 'is-recording': isRecording }">
            {{ isRecording ? '⏹' : '🎙️' }}
          </i>
          <span class="button-text">
            {{ isRecording ? 'Stop Recording' : isProcessing ? 'Processing...' : 'Record Your Voice' }}
          </span>
        </div>
        </button>

        <div v-if="recordingExists" class="recording-actions">
          <button class="action-button preview-button" @click="playPreview" :disabled="isPreviewPlaying">
            {{ isPreviewPlaying ? '⏸' : '▶' }}
          </button>
          <button class="action-button save-button" @click="saveRecording" :disabled="isProcessing">
            💾
          </button>
          <button class="action-button delete-button" @click="deleteRecording">
            🗑️
          </button>
        </div>
      </div>

      <div v-if="isRecording" class="recording-indicator">
        <div class="record-wave">
          <div v-for="n in 5" :key="n" class="wave-bar" :style="{ backgroundColor: speakerColor }"></div>
        </div>
        <span class="recording-timer" :style="{ color: speakerColor }">{{ formattedTime }}</span>
      </div>

      <div v-if="recordingExists" class="recording-info">
        <span class="recording-length">{{ recordingDuration }}s</span>
        <div class="recording-waveform">
          <!-- Simple placeholder for waveform visualization -->
          <div v-for="n in 20" :key="n"
            class="waveform-bar"
            :style="{
              height: getRandomHeight() + 'px',
              backgroundColor: speakerColor
            }"
          ></div>
        </div>
      </div>
    </div>
  </template>

  <script>
  import API from '@/api';
  import { mapActions } from 'vuex';
  export default {
    name: 'VoiceRecorder',
    props: {
      segmentIndex: {
        type: Number,
        required: true
      },
      isPro: {
        type: Boolean,
        default: false
      },
      segment: {
        type: Object,
        required: true
      },
      messageId: {
        required: true
      },
      speakerColor: {
        type: String,
        default: '#e53e3e' // Default to red if no color is provided
      }
    },
    data() {
      return {
        isRecording: false,
        isProcessing: false,
        recordingExists: false,
        isPreviewPlaying: false,
        mediaRecorder: null,
        audioChunks: [],
        audioBlob: null,
        audioUrl: null,
        previewAudio: null,
        startTime: null,
        recordingTime: 0,
        recordingDuration: 0,
        timerInterval: null
      }
    },
    computed: {
      formattedTime() {
        const minutes = Math.floor(this.recordingTime / 60);
        const seconds = this.recordingTime % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }
    },
    methods: {
      ...mapActions('websocket', ['showTempMessageFn']),
      async toggleRecording() {
        if (!this.isPro) {
          this.showTempMessageFn({
            message: 'This feature is only available for Pro users.',
            type: 'warning'
          });
          return;
        }
        if (this.isRecording) {
          this.stopRecording();
        } else {
          await this.startRecording();
        }
      },

      async startRecording() {
        try {
          // Reset state
          this.audioChunks = [];
          this.recordingTime = 0;

          // Request microphone access
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

          // Create media recorder
          this.mediaRecorder = new MediaRecorder(stream);

          // Set up event handlers
          this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              this.audioChunks.push(event.data);
            }
          };

          this.mediaRecorder.onstop = () => {
            // Create a blob from the recorded chunks
            this.audioBlob = new Blob(this.audioChunks, { type: 'audio/mpeg' });

            // Create a URL for the blob
            if (this.audioUrl) {
              URL.revokeObjectURL(this.audioUrl);
            }
            this.audioUrl = URL.createObjectURL(this.audioBlob);

            // Set up preview audio
            this.previewAudio = new Audio(this.audioUrl);
            this.previewAudio.onended = () => {
              this.isPreviewPlaying = false;
            };

            // Update state
            this.recordingExists = true;
            this.recordingDuration = Math.round(this.recordingTime);
            this.isRecording = false;
            this.isProcessing = false;

            // Stop all tracks in the stream
            stream.getTracks().forEach(track => track.stop());
          };

          // Start recording
          this.mediaRecorder.start();
          this.isRecording = true;
          this.startTime = Date.now();

          // Start timer
          this.timerInterval = setInterval(() => {
            this.recordingTime = Math.floor((Date.now() - this.startTime) / 1000);
          }, 1000);
        } catch (error) {
          console.error('Error starting recording:', error);
          alert('Failed to access microphone. Please check your permissions.');
          this.isRecording = false;
        }
      },

      stopRecording() {
        if (this.mediaRecorder && this.isRecording) {
          this.mediaRecorder.stop();
          clearInterval(this.timerInterval);
          this.isProcessing = true;
        }
      },

      playPreview() {
        if (this.isPreviewPlaying) {
          this.previewAudio.pause();
          this.isPreviewPlaying = false;
        } else if (this.previewAudio) {
          this.previewAudio.currentTime = 0;
          this.previewAudio.play();
          this.isPreviewPlaying = true;
        }
      },

      async saveRecording() {
        if (this.audioBlob) {
          this.isProcessing = true;

          try {
            // Create a FormData object for the file upload
            const formData = new FormData();

            // Add the audio file with the correct name
            formData.append('file', this.audioBlob, 'recording.mp3');

            // Add the duration as a form field
            formData.append('duration', this.recordingDuration.toString());

            // Upload to the server using FormData
            const response = await API.post(
              `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/voice`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            );

            // Extract the server-assigned URL
            const serverUrl = response.data.url;

            // Emit event with recording data
            this.$emit('recording-saved', {
              segmentIndex: this.segmentIndex,
              audioBlob: this.audioBlob,
              audioUrl: serverUrl, // Use the server URL
              duration: this.recordingDuration
            });

            console.log('Recording saved to server successfully');
          } catch (error) {
            console.error('Error saving recording to server:', error);
            alert('Failed to save recording. Please try again.');
          } finally {
            this.isProcessing = false;
          }
        }
      },

      async deleteRecording() {
        // First clean up local resources
        if (this.previewAudio) {
          this.previewAudio.pause();
        }

        if (this.audioUrl) {
          // Only revoke the URL if it's a blob URL (not a server URL)
          if (this.audioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.audioUrl);
          }
        }

        this.isProcessing = true;

        try {
          // Delete from server
          await API.delete(
            `/user_customization/messages/${this.messageId}/segments/${this.segmentIndex}/voice`
          );

          // Reset local state
          this.audioChunks = [];
          this.audioBlob = null;
          this.audioUrl = null;
          this.previewAudio = null;
          this.recordingExists = false;
          this.isPreviewPlaying = false;

          // Emit event to notify parent that recording was deleted
          this.$emit('recording-deleted', this.segmentIndex);

          console.log('Recording deleted from server successfully');
        } catch (error) {
          console.error('Error deleting recording from server:', error);
          // Show error message to user
          alert('Failed to delete recording. Please try again.');
        } finally {
          this.isProcessing = false;
        }
      },
      // Helper method for waveform visualization
      getRandomHeight() {
        return Math.floor(Math.random() * 15) + 5;
      }
    },
    beforeDestroy() {
      if (this.previewAudio) {
        this.previewAudio.pause();
      }

      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl);
      }

      clearInterval(this.timerInterval);
    }
  }
  </script>

  <style scoped>
  .voice-recorder {
    margin-top: 1rem;
    padding: 0.75rem;
    border-radius: 0.5rem;
    background-color: rgba(0, 0, 0, 0.05);
  }

  .recorder-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .record-button {
    flex: 1;
    padding: 0.625rem 1rem;
    border: none;
    border-radius: 0.375rem;
    background-color: #e53e3e;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .record-button:hover:not(:disabled) {
    background-color: #c53030;
  }

  .record-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .record-button.is-recording {
    animation: pulse 1.5s infinite;
  }

  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .record-icon {
    font-size: 1.25rem;
  }

  .record-icon.is-recording {
    color: white;
  }

  .recording-actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-button {
    width: 2.5rem;
    height: 2.5rem;
    border: none;
    border-radius: 0.375rem;
    background-color: #4a5568;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .action-button:hover:not(:disabled) {
    opacity: 0.9;
  }

  .action-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .preview-button {
    background-color: #4299e1;
  }

  .save-button {
    background-color: #48bb78;
  }

  .delete-button {
    background-color: #e53e3e;
  }

  .recording-indicator {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.75rem;
  }

  .record-wave {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .wave-bar {
    width: 3px;
    height: 15px;
    background-color: #e53e3e;
    border-radius: 1px;
    animation: soundBars 1.2s ease-in-out infinite;
  }

  .wave-bar:nth-child(1) { animation-delay: 0s; }
  .wave-bar:nth-child(2) { animation-delay: 0.2s; }
  .wave-bar:nth-child(3) { animation-delay: 0.4s; }
  .wave-bar:nth-child(4) { animation-delay: 0.6s; }
  .wave-bar:nth-child(5) { animation-delay: 0.8s; }

  .recording-timer {
    font-family: monospace;
    color: #e53e3e;
    font-weight: bold;
  }

  .recording-info {
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .recording-length {
    font-size: 0.875rem;
    color: #4a5568;
  }

  .recording-waveform {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 30px;
  }

  .waveform-bar {
    flex: 1;
    background-color: #4299e1;
    border-radius: 1px;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }

  @keyframes soundBars {
    0%, 100% { transform: scaleY(0.3); }
    50% { transform: scaleY(1); }
  }

  /* Media query for smaller screens */
  @media (max-width: 768px) {
    .recorder-controls {
      flex-direction: column;
    }

    .recording-actions {
      width: 100%;
      justify-content: space-around;
      margin-top: 0.5rem;
    }
  }
  </style>
